import { Button } from "@caprikorn/dynamo-ui";
import { getFullAddress, getFullName } from "@caprikorn/dynamo-utils";
import { useFlags } from "@flagsmith/flagsmith/react";
import { useEffect, useEffectEvent, useState } from "react";
import { useParams } from "react-router-dom";

import { OverviewContainer } from "@components/OverviewContainer/OverviewContainer";
import type { PerimeterSection } from "@components/PerimeterLayout/PerimeterLayout.types";
import { useGroupReferences } from "@contexts/GroupReferences/GroupReferences.context";
import { useReferences } from "@contexts/References/References.context";
import { useSelectedPerimeters } from "@contexts/SelectedPerimeters/SelectedPerimeters.context";
import type { Flags } from "@core/flagsmith/flags.types";
import { usePerimeterConsoAction } from "@hooks/usePerimeterConsoAction";
import { EditPencil01 } from "@icons";
import type { UrlParam } from "@router/router.types";
import { useGetCustomer } from "@services/apiEcms/useGetCustomer";
import { useGetCustomerContract } from "@services/apiEcms/useGetCustomerContract";
import { useGetGroup } from "@services/apiEcms/useGetGroup";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import { AddressingCodeDrawer } from "./AddressingCodeDrawer/AddressingCodeDrawer";
import { InvoiceAndPaymentsDetails } from "./InvoiceAndPaymentsDetails/InvoiceAndPaymentsDetails";
import classes from "./PerimeterSites.module.css";
import { PerimeterSitesTable } from "./PerimeterSitesTable/PerimeterSitesTable";

export const PerimeterSites = () => {
  const { t } = useCustomTranslation("main");
  const { "show-invoice": showInvoice, "show-e-invoicing": showEInvoicing } =
    useFlags<Flags>(["show-invoice", "show-e-invoicing"]);
  const { currentGroup } = useGroupReferences();
  const { customerId = "", contractId = "" } = useParams<UrlParam>();

  const { setReferencesFunc, references } = useReferences();
  const { setSelectedPerimeters } = useSelectedPerimeters();

  const { isLoading } = useGetGroup({
    groupId: currentGroup,
  });
  const perimeterNavLevelName = references.contract?.name;

  // Tied to the contract it was opened for, so it closes when contractId changes
  const [addressingCodeDrawerContractId, setAddressingCodeDrawerContractId] =
    useState<string>();
  const isAddressingCodeDrawerOpen =
    addressingCodeDrawerContractId === contractId;

  const { data: customer } = useGetCustomer(customerId);
  const { data: contract } = useGetCustomerContract({ customerId, contractId });

  const customerRefId = customer?.customerId;
  const customerName = customer?.companyName;
  const contractRefId = contract?.contractInformation.contractId;
  const contractName = contract?.contractInformation.contractName;

  const syncReferences = useEffectEvent(
    (newCustomerId: string, newContractId: string) => {
      setReferencesFunc({
        customer: {
          id: newCustomerId,
          name: customerName,
        },
        contract: {
          id: newContractId,
          name: contractName,
        },
      });
    },
  );

  const resetReferences = useEffectEvent(() => {
    setReferencesFunc();
    setSelectedPerimeters([]);
  });

  useEffect(() => {
    if (customerRefId && contractRefId) {
      syncReferences(customerRefId, contractRefId);
    }
    return () => resetReferences();
  }, [customerRefId, contractRefId]);

  const { gas, elec, energiesLoading } = usePerimeterConsoAction({
    isSearch: false,
    customerId,
    contractId,
  });

  const contactData: PerimeterSection = {
    title: t("perimeter.overview.site.contact"),
    data: [
      {
        name: t("perimeter.overview.contact.name"),
        value: getFullName(
          contract?.billingContact ? contract.billingContact : {},
        ),
      },
      {
        name: t("perimeter.overview.contact.email"),
        value: contract?.billingContact.email,
      },
      {
        name: t("perimeter.overview.contact.address"),
        value: getFullAddress(
          contract?.billingAddress ? contract.billingAddress : {},
        ),
      },
    ].filter((item) => Boolean(item.value)),
  };

  const eInvoicing =
    showEInvoicing.enabled && customer && contract
      ? {
          isVatTaxable: customer.isVatTaxable !== false,
          addressingCode: contract.contractInformation.addressingCode,
        }
      : undefined;

  const getAddressingCodeValue = () => {
    if (!eInvoicing) {
      return undefined;
    }
    if (!eInvoicing.isVatTaxable) {
      return t("perimeter.overview.site.eInvoicing.notVatTaxable");
    }
    return (
      eInvoicing.addressingCode ||
      t("perimeter.overview.site.eInvoicing.notProvided")
    );
  };

  const canEditAddressingCode = eInvoicing?.isVatTaxable === true;

  const levelData: PerimeterSection = {
    title: t("perimeter.overview.site.siteTitle"),
    data: [
      {
        name: t("perimeter.contracts.contractReference"),
        value: contract?.contractInformation.contractId,
      },
      {
        name: t("perimeter.overview.site.costCenter"),
        value: contract?.contractInformation.costCenter || "",
      },
      {
        name: t("perimeter.overview.site.service"),
        value: contract?.contractInformation.service,
      },
      {
        name: t("perimeter.overview.site.eInvoicing.addressingCode"),
        value: getAddressingCodeValue(),
      },
    ].filter((item) => Boolean(item.value)),
  };

  return (
    <div className={classes.container}>
      <OverviewContainer
        areEnergiesLoading={energiesLoading}
        contactData={contactData.data.length > 0 ? contactData : undefined}
        elec={elec}
        gas={gas}
        isGroupLoading={isLoading}
        levelAction={
          canEditAddressingCode && (
            <Button
              aria-label={t("perimeter.overview.site.eInvoicing.editAriaLabel")}
              onClick={() => setAddressingCodeDrawerContractId(contractId)}
              startIcon={EditPencil01}
              variant="tertiary"
            >
              {t("perimeter.overview.site.eInvoicing.edit")}
            </Button>
          )
        }
        levelData={levelData.data.length > 0 ? levelData : undefined}
        perimeterNavLevelName={perimeterNavLevelName}
      />
      {showInvoice.enabled && contractId && <InvoiceAndPaymentsDetails />}
      <PerimeterSitesTable />
      {isAddressingCodeDrawerOpen && (
        <AddressingCodeDrawer
          contractId={contractId}
          customerId={customerId}
          onClose={() => setAddressingCodeDrawerContractId(undefined)}
        />
      )}
    </div>
  );
};
